import React, { useState } from "react";
import { Box, Typography, useMediaQuery } from '@mui/material';
import Architecture from './../images/Projects/TidyNestSystemArchitecture.png';
import Model from './../images/Projects/TidyNestDataModel.png';
import TidyNestClient from './../images/Projects/tidynest/tidy1.PNG';
import TidyNestClient1 from './../images/Projects/tidynest/tidy2.PNG';
import Admin1 from './../images/Projects/tidynest/Admin1.png';
import Admin2 from './../images/Projects/tidynest/Admin2.png';
import TidyNestCleaner1 from './../images/Projects/tidynest/tidynest3.PNG';
import TidyNestCleaner2 from './../images/Projects/tidynest/tidynest4.PNG';
import TidyNestCleaner4 from './../images/Projects/tidynest/tidynest6.PNG';
import TidyNestCleaner5 from './../images/Projects/tidynest/tidynest7.PNG';

const TidyNestProjectInformation = () => {
  const desktopView = useMediaQuery('(min-width:1093px)');
  const [activeView, setActiveView] = useState("user");

  const tabStyle = (isActive) => ({
    cursor: "pointer",
    paddingBottom: "8px",
    fontSize: "16px",
    fontWeight: isActive ? 500 : 400,
    letterSpacing: "0.3px",
    color: isActive ? "#111" : "#777",
    borderBottom: isActive ? "2px solid #111" : "2px solid transparent",
    transition: "all 0.2s ease"
  });

  const tags = [
    "ai",
    "computer vision",
    "spatial intelligence",
    "data modelling",
    "systems thinking",
    "vibe coding",
    "empathy",
    "product thinking"
  ];

  const technicalContent = `
    <span>This section focuses on the <b>behind-the-scenes systems</b> that power the client-side of the system. See this as a technical lens, for more details, should read the user section first</span>
    
    <h3>Key Considerations</h3>
    <p>Honestly, building and training computer vision models from scratch is resource-intensive.</p>
    
    <h3>Technical Decisions</h3>
    <p> So for this purpose since we're focused on validating just the problem and focus on other business logics. <a href="https://ai.azure.com/explore/aiservices">Microsoft Azure AI</a> services would be used for all computer vision functionality within TidyNest. This decision would allow TidyNest to leverage an existing scalable, well-supported vision capabilities while keeping the core product focused on other business requirements.</p>
    
    <p>It would be applied in these three key areas in TidyNest:</p>
    <ul>
      <li>Facial enrollment of cleaners</li>
      <li>Cleaner's Facial verification during service check-in</li>
      <li>Assessment of cleaning quality using before-and-after images</li>
    </ul>
    <p>These help in supporting TidyNest's core principles of <b> <i> quality, reliability, and trust  </i> </b>  to customers.</p>
    
    <h3>System Architecture Overview</h3>
    <p>Here I'll share the system architecture which illustrates the high-level interactions between cleaners (focusing on just them here) and the system, also highlighting the specific points in the user journey we rely on computer vision service from Azure.</p>
    
    <figure style="width:90%; max-width:900px; margin:30px auto; text-align:center;">
      <img src="${Architecture}" alt="System Architecture Diagram" style="width: 100%; max-width: 900px; height: auto; display: block; margin: 30px auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);" />
      <figcaption style="margin-top:8px; font-size:16px; color:#555;">
        Architecture Illustration. <a href="https://drive.google.com/file/d/1Nj82M7JqpBkqOjLGSNZOwCOsiLZJ5Fb_/view?usp=sharing"> Click here to view</a>
      </figcaption>
    </figure>
    
    <h4>Facial Enrollment of Cleaners</h4>
    <p>During account setup of a cleaner, they are required to do a facial enrollment which uses the Azure Face API. These facial images would be submitted to a configured PersonGroup in Azure. Azure would return a unique identifier, which would be stored in the TidyNest system as a reference and used solely for verification purposes. Also the <b> <i> detection_03 model </i> </b> would be used due to its robustness across varied real-world conditions that are relevant to on-site service work.</p>
    
    <h4>Facial Verification During Check-In</h4>
    <p>At check-in, the <b> <i> <a href='https://learn.microsoft.com/en-us/rest/api/face/face-recognition-operations/verify-face-to-face?view=rest-face-v1.2&tabs=HTTP'> Azure Face Verification </a> </i> </b>  operation would help to compare the detected face against the enrolled reference for the assigned cleaner. The API returns a confidence score indicating whether the identities match.</p>
    
    <h4>Assessment of Work Done by Cleaners</h4>
    <p> <b> <i> <a href='https://learn.microsoft.com/en-us/azure/ai-services/custom-vision-service/overview'> Azure AI Custom Vision </a> </i> </b> would be used to assess cleaning quality through before-and-after images submitted for a service by the cleaner.</p>
    <p><b>Note:</b> Away from the client-side, the custom vision AI model would be trained with labeled data of clean and unclean spaces or environments, which would enable it to classify the condition of a space.</p>
    
    <h3>Data Modeling</h3>
    <figure style="width:90%; max-width:900px; margin:30px auto; text-align:center;">
      <img src="${Model}" alt="Data Model Diagram" style="width: 500px; max-width: 2000px; height: 500px; display: block; margin: 30px auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);" />
      <figcaption style="margin-top:8px; font-size:16px; color:#555;">
        Illustration of the Data Model. <a href="https://drive.google.com/file/d/1V8wiqKcsuEHTPwzWDVkrv5N9mQV54A6b/view?usp=sharing">Click here to view</a>
      </figcaption>
    </figure>
    
    <p>TidyNest internal data model would focus on supporting seamless management and external integration. The system consists of three core entities:</p>
    <ul>
      <li><b>Cleaners:</b> this table stores cleaner identity, enrollment status, and external vision references</li>
      <li><b>Requested Services:</b> this table holds the customers requests and their status</li>
      <li><b>Activity:</b> Helps in storing the real-world events such as check-ins, verification outcomes, and timestamps</li>
    </ul>
    
    <p>The relationships are defined as follows:</p>
    <ul>
      <li>One cleaner can be assigned to multiple requested services</li>
      <li>Each requested service is assigned to a single cleaner</li>
      <li>One cleaner can have multiple activity records (e.g., multiple check-ins over time)</li>
    </ul>

    
  `;

  const userContent = `
    <h3>About TidyNest</h3>
    <p>TidyNest provides on-demand cleaning services delivered by trained professionals, designed to be flexible, reliable, and easy to manage for both customers and cleaners.</p>
    
    <h3>Customer Problems:</h3>
    <ul>
      <li>Busy individuals and professionals often lack the time or energy to clean their homes and would rather use their limited free time to rest.</li> 
      <li>Startups with hybrid or infrequent office use still require periodic cleaning without maintaining full-time staff.</li>
      <li>Short-let and Airbnb owners need reliable cleaning between guest check-ins and check-outs to maintain standards and reviews.</li>
    </ul>
    
    <h3>What These Customers Have in Common</h3>
    <p>Across these groups, they all have similar needs:</p>
    <ul> 
      <li>On-demand and flexible service</li>
      <li>No long-term commitment</li>
      <li>Reliability and consistency</li>
      <li>Trust in who enters their space</li>
      <li>Clear communication and quality assurance</li>
    </ul>

    <h3>Problem Overview</h3>
    <p>On-demand cleaning services are often unreliable. Cleaners may cancel, arrive late, or deliver inconsistent results. From the agency side, coordinating workers across different locations, schedules, and service expectations is operationally complex.</p>
    
    <h3>TidyNest Offering</h3>
    <p>TidyNest addresses this by providing structured, professionally managed cleaning services that customers can rely on when they need them without requiring permanent staff or constant supervision.</p>
    
    <h3>TidyNest Focus and Principles</h3>
    <ul>
      <li><b>Quality:</b> Cleaners are professionally trained and services follow defined standards.</li>
      <li><b>Trust:</b> Customers don't need to worry about who enters their space or supervises work on-site.</li>
      <li><b>Reliability:</b> Jobs are assigned intentionally with verified workers.</li>
    </ul>
    <p><b>Note:</b> Technology supports these principles but does not replace human oversight.</p>

    <h3>User Groups Use Cases</h3>
    
    <h3>Customers</h3>
    <p><b>1. Request For Services:</b> Customers can request cleaning services by providing, no account creation needed:</p>
    <ul>
      <li>Location (selected via Google Maps) in a dropdown on the UI</li>
      <li>Date and time of when the service is needed</li>
      <li>Type of service</li>
      <li>Preferences and special instructions</li>
      <li>Contact details (email and phone number)</li>
      <li>Images or videos of the space to be cleaned: <i>This is solely for quotation of price for the service.</i></li>
    </ul>
    
    <p><b>2. Real-Time Notifications of Service:</b> Customers receive email updates when:</p>
    <ul>
      <li>Their request is assigned to a cleaner</li>
      <li>The cleaner checks in to start work</li>
      <li>The cleaner completes the job, along with a service summary</li>
    </ul>
   <figure style="
  width:90%;
  max-width:900px;
  margin:30px auto;
  text-align:center;
">
  <div style="
    display:flex;
    gap:24px;
    justify-content:center;
    align-items:center;
    flex-wrap:wrap;
  ">
    <img
      src="${TidyNestClient}"
      alt="DScreenshot of a Customer Trying to Request for a Cleaning Service."
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
    <img
      src="${TidyNestClient1}"
      alt="Screenshot of a Customer Trying to Request for a Cleaning Service."
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
  </div>

  <figcaption style="margin-top:12px; font-size:16px; color:#555;">
    Screenshot of a Customer Trying to Request for a Cleaning Service.
  </figcaption>
</figure>

    <h3>Workers / Cleaners</h3>
    <p><b>1. Application to Join Tidynest Cleaners:</b> Cleaners can apply by submitting basic personal details for assessment by tidynest.</p>
    <p><b>2. Onboarding/Enrollment:</b> Approved cleaners complete onboarding by setting up credentials, submitting facial images for identity verification, and selecting their place of residence from mapped locations on google maps</p>
    <p><b>3. Cleaning Service Execution</b></p>
    <ul>
      <li>Cleaners see assigned jobs in their dashboard.</li>
      <li>On arrival, they check in by confirming their location and verifying their identity.</li>
      <li>The system records check-in time and location.</li>
    </ul>
  
     <figure style="
  width:90%;
  max-width:900px;
  margin:30px auto;
  text-align:center;
">
  <div style="
    display:flex;
    gap:24px;
    justify-content:center;
    align-items:center;
    flex-wrap:wrap;
  ">
    <img
      src="${TidyNestCleaner1}"
      alt="Screenshot of a Customer Trying to Request for a Cleaning Service."
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
    <img
      src="${TidyNestCleaner2}"
      alt="Screenshot of a Customer Trying to Request for a Cleaning Service."
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
  </div>

  <figcaption style="margin-top:12px; font-size:16px; color:#555;">
    Screenshot of a Cleaner Trying to CheckIn by Verifying their Location & Identity.
  </figcaption>
</figure>
    
    <p><b>4. Quality Assessment</b></p>
    <ul>
      <li>Before starting, cleaners capture images of the current state of the space.</li>
      <li>After completing the job, they submit matching images to document and analyze the completed work.</li>
    </ul>
    <figure style="
  width:90%;
  max-width:900px;
  margin:30px auto;
  text-align:center;
">
  <div style="
    display:flex;
    gap:24px;
    justify-content:center;
    align-items:center;
    flex-wrap:wrap;
  ">
    <img
      src="${TidyNestCleaner5}"
      alt="Screenshot of a Customer Trying to Request for a Cleaning Service."
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
    <img
      src="${TidyNestCleaner4}"
      alt="Screenshot of a Customer Trying to Request for a Cleaning Service."
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
  </div>

  <figcaption style="margin-top:12px; font-size:16px; color:#555;">
    Screenshot of a Process of Assessing WOrk Done.
  </figcaption>
</figure>

    <h3>TidyNest Admin Use Cases</h3>
    <ul>
      <li>Review and manage service requests</li>
      <li>Assign cleaners based on location and availability</li>
      <li>Visualize spatial distribution of requested services and workforce</li>
      <li>Review completed jobs and quality reports</li>
    </ul>
     <figure style="
  width:90%;
  max-width:900px;
  margin:30px auto;
  text-align:center;
">
  <div style="
    display:flex;
    gap:24px;
    justify-content:center;
    align-items:center;
    flex-wrap:wrap;
  ">
    <img
      src="${Admin1}"
      alt="Screenshot of TidyNest Admin Dashboard"
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
    <img
      src="${Admin2}"
      alt="Screenshot of TidyNest Admin Dashboard."
      style="
        width:100%;
        max-width:420px;
        height:auto;
        border-radius:8px;
        box-shadow:0 4px 8px rgba(0,0,0,0.1);
      "
    />
  </div>

  <figcaption style="margin-top:12px; font-size:16px; color:#555;">
    Screenshot of TidyNest Admin Dashboard.
  </figcaption>
</figure>
    
    <h3>Future Usecases For Exploration:</h3>
    <ul> 
      <li><b>Automatic pricing:</b> Customers can upload images or videos of a space to receive estimated pricing based on size and condition, before they can proceed to request for a cleaning service.</li>
      <li><b>Optional monitoring:</b> Customers may choose an additional monitoring feature which would be with their explicit consent, further enabling reliable service to be delivered to them. This feature would require carefully balancing both its usefulness and customers and cleaners privacy.</li>
    </ul>

    <h3>Metrics</h3>
    <ul>
      <li><b>Face check-in failure or retry rate:</b> How often cleaners are unable to successfully complete facial check-in on the first attempt, and how many retries are required before success. Facial verification shouldn't be an inconvenience in harsh fast moved reality, hence it would help confirm its usability.</li>
      <li><b>Quality Confidence vs Customer Satisfaction:</b> Understand if the system's quality assessment also reflects the customer's satisfaction</li>
      <li><b>Successful Service Fulfillment Rate:</b> The percentage of requested services that are completed as scheduled by the assigned cleaner, at the correct location and time, showing professionalism.</li>
      <li><b>Customer repeat rate:</b>Do customers come back or use the service just once? </li>
      <li><b> Cleaner retention rate:</b></li> Are cleaners staying with tidynest over time? </li>
      <li><b> Reassignment rate: </b> How often jobs need to be reassigned due to cleaner cancellations or no showing up</li>
      <li> <b> Time-to-assignment:</b> Average time taken from when a customer requests a service to when a cleaner is confirmed and assigned to the job </li>
      </ul>

    <h3>Responsible Use of AI Considerations</h3>
    <ul>
      <li>We only collect data that's necessary for the service, and we tell users upfront why we need it. Their trust matters and we've built our system to be accountable without invading their privacy.</li>
      <li>Facial enrollment is only available to cleaners who have been verified through our application assessment process.</li>
      <li>We always ask for explicit permission at every step where personal information or knowledge is involved either as a customer or a cleaner.</li>
      <li>Quality assessment data isn't fed into automated systems for performance scores or behavioral tracking. TidyNest administrators have a feature to personally review all images and assessments before making any decisions, ensuring fairness and context.</li>
    </ul>

    <h2> Known Limitations & Concerns </h2>
    <p> Technology can help in service delivery, but there are certain real-life conditions that are out of technology's control. Here are some that are expected: </p>
    <p> <b> 1. Facial verification/Work Assessment in low or no light: </b> Facial checks depend on lighting. When there isn’t enough light or even internet access, the cleaner proceeds with the job and completion is confirmed through customer feedback or reviews. This is treated as an environmental limitation, not an error or system incapabiity. </p>
    <p> <b> 2. Cleaner safety: </b> Cleaner safety goes beyond technology. Customers are vetted outside the system before accepting their requested service. Also, for cleaners checking in and out of jobs, and if a check-out is not done after a particular time of checkin, the team can be automatically notified
   and  follows up to ensure everything is okay.  </p>
    <p> <b> 3. Last-minute replacements: </b> If a cleaner cancels, another cleaner can be reassigned as the replacement internally.</p>
  `; 

  const contentStyle = {
    fontSize: '18px',
    lineHeight: '2',
    marginBottom: '16px',
    marginLeft: desktopView ? '150px' : '20px',
    marginRight: desktopView ? '150px' : '20px'
  };

  return (
    <Box>
      <br /><br /><br /><br />
      <Typography style={{ fontFamily: "Oswald", textAlign: 'center', fontSize: '20px' }}>
        <u>TIDYNEST: A CASETUDY OF VALIDATING TRUST AND QUALITY IN ON-DEMAND SERVICES WITH COMPUTER VISION & SPATIAL DATA</u>
      </Typography>
      <br />
      
      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "40px",
          marginBottom: "40px"
        }}
      >
        <div
          style={tabStyle(activeView === "user")}
          onClick={() => setActiveView("user")}
        >
          User
        </div>

        <div
          style={tabStyle(activeView === "technical")}
          onClick={() => setActiveView("technical")}
        >
          Technical
        </div>
      </div>
      
      
     <div
  style={{
    display: "flex",
    justifyContent: "center",
    marginTop: "24px", // pushes it below the header
  }}
>
  <div
    style={{
      position: "relative",
      width: "100%",
      maxWidth: "720px", // tighter width = less height
      aspectRatio: "16 / 9", // modern, cleaner than padding hacks
    }}
  >
    <iframe
      src="https://www.youtube.com/embed/0g2ipXG-UZQ"
      title="TidyNest Product Demo"
      style={{
        width: "100%",
        height: "100%",
        border: 0,
      }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  </div>
</div>


   
     

      {/* Content Area */}
      <div
        style={contentStyle}
        dangerouslySetInnerHTML={{
          __html: activeView === "technical" ? technicalContent : userContent
        }}
      />

      {/* Tags */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginTop: '20px',
        padding: desktopView ? '0 150px' : '0 10px'
      }}>
        <Typography style={contentStyle}>
          <b style={{textAlign: 'center'}}> <ul> DISCLAIMER! </ul>  </b>
<p> The demo application is entirely vibecoded using <a target="_blank" rel="noopener noreferrer"  href='https://aistudio.google.com'> Google AI Studio </a> and serves as a simulation, 
  demonstrating how computer vision combined with spatial data could enhance operational efficiency and deliver reliable, high-quality service. </p>
        </Typography>
        {tags.map((tag, index) => (
          <Typography
            key={index}
            style={{
              display: 'inline-block',
              border: '1px solid #d0d0d0',
              borderRadius: '999px',
              padding: '6px 14px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              fontSize: '16px'
            }}
          >
            {tag}
          </Typography>
        ))}
      </div>
      
      <br />
    </Box>
  );
};

export default TidyNestProjectInformation;